import { createAsyncThunk } from '@reduxjs/toolkit';
import { type IErrorData, getErrorData } from '../../../utils/getErrorData';
import { isError } from '../../../utils/typeguards/isError';
import {
  getVendorOrderOffers,
  updateVendorOrderOfferVendor
} from '../../../api/vendorApi';
import {
  getVendorNames,
  getOffersCountByStatusAndVendor
} from '../../../hooks/orderOfferApi';
import {
  type IGetVendorOffersParams,
  type IVendorOrderItem
} from '../../../types/IVendorOrderResponse';
import { type IVendorItem } from '../../../types/IFetchOrderVendorParams';
import { type RootState } from '../../store';
import {
  IOffersByStatusResponse,
  OfferStatus
} from '@/types/IVendorWithCountOffers';

export type IGetVendorOrderOffersParams = Omit<
  IGetVendorOffersParams,
  'signal'
>;

export interface IVendorOrderOffersResult {
  items: IVendorOrderItem[];
  total: number;
  requestedOffset: number;
}

export const getVendorOrderOffersThunk = createAsyncThunk<
  IVendorOrderOffersResult,
  IGetVendorOrderOffersParams,
  { rejectValue: IErrorData }
>(
  'vendors/getVendorOrderOffersThunk',
  async (params, { rejectWithValue, signal }) => {
    const offset = params.offset ?? 0;
    const limit = params.limit ?? 50;
    const only_wait_action = params.only_wait_action;

    try {
      const data = await getVendorOrderOffers({
        ...params,
        offset,
        limit,
        only_wait_action,
        signal
      });

      if (data === 'Abort') {
        return rejectWithValue({ message: 'Запрос был отменен' });
      }

      if (isError(data)) {
        return rejectWithValue(data);
      }

      if (data.response === null) {
        return rejectWithValue({ message: 'Некорректный ответ сервера' });
      }

      return {
        items: data.response.items,
        total: data.response.meta.total,
        requestedOffset: offset
      };
    } catch (error) {
      const errorData = getErrorData(error);
      return rejectWithValue(errorData);
    }
  }
);

// Санка для навигации по страницам
export const navigateToPageThunk = createAsyncThunk<
  IVendorOrderOffersResult,
  { offset: number; limit: number; only_wait_action?: boolean },
  { rejectValue: IErrorData; state: RootState }
>(
  'vendors/navigateToPageThunk',
  async (
    { offset, limit, only_wait_action },
    { rejectWithValue, signal, getState }
  ) => {
    const state = getState();
    const { totalItems } = state.vendors.pagination;

    if (offset >= totalItems && totalItems > 0) {
      return {
        items: [],
        total: totalItems,
        requestedOffset: offset
      };
    }

    try {
      const data = await getVendorOrderOffers({
        offset,
        limit,
        only_wait_action,
        signal
      });

      if (data === 'Abort') {
        return rejectWithValue({ message: 'Запрос был отменен' });
      }

      if (isError(data)) {
        return rejectWithValue(data);
      }

      if (data.response === null) {
        return rejectWithValue({ message: 'Некорректный ответ сервера' });
      }

      return {
        items: data.response.items,
        total: data.response.meta.total,
        requestedOffset: offset
      };
    } catch (error) {
      const errorData = getErrorData(error);
      return rejectWithValue(errorData);
    }
  }
);

// Санка для получения списка поставщиков
export const getVendorNamesThunk = createAsyncThunk<
  IVendorItem[],
  undefined,
  { rejectValue: IErrorData }
>('vendors/getVendorNamesThunk', async (_, { rejectWithValue }) => {
  try {
    const data = await getVendorNames();

    return data;
  } catch (error) {
    const errorData = getErrorData(error);
    return rejectWithValue(errorData);
  }
});

// Санка для обновления поставщика торгового предложения
export const updateVendorOrderOfferVendorThunk = createAsyncThunk<
  IVendorOrderItem,
  { order_offer_uuid: string; new_vendor_id: string },
  { rejectValue: IErrorData; state: RootState }
>(
  'vendors/updateVendorOrderOfferVendor',
  async ({ order_offer_uuid, new_vendor_id }, { rejectWithValue, signal }) => {
    try {
      const data = await updateVendorOrderOfferVendor({
        order_offer_uuid,
        new_vendor_id,
        signal
      });

      if (data === 'Abort') {
        return rejectWithValue({ message: 'Запрос был отменен' });
      }

      if (isError(data)) {
        return rejectWithValue(data);
      }

      if (data.response === null) {
        return rejectWithValue({ message: 'Некорректный ответ сервера' });
      }

      return data.response;
    } catch (error) {
      const errorData = getErrorData(error);
      return rejectWithValue(errorData);
    }
  }
);

// Санка для получения количества офферов по статусу и поставщику
export const getOffersCountByStatusAndVendorThunk = createAsyncThunk<
  IOffersByStatusResponse,
  OfferStatus,
  { rejectValue: IErrorData }
>(
  'vendors/getOffersCountByStatusAndVendor',
  async (offerStatus, { rejectWithValue }) => {
    try {
      const data = await getOffersCountByStatusAndVendor(offerStatus);

      if (isError(data)) {
        return rejectWithValue(data);
      }

      return data;
    } catch (error) {
      const errorData = getErrorData(error);
      return rejectWithValue(errorData);
    }
  }
);
